import { useEffect, useState } from 'react'
import { Form, Upload, message, Input, Select } from 'antd'
import { Switch, Drawer } from '@/components/ui'
import { UploadOutlined } from '@ant-design/icons'
import { router } from '@inertiajs/react'
import axios from '@/lib/axios'
import type { UploadFile } from 'antd'
import '@/styles/compact-form.css'

type SpeakerRole = 'speaker' | 'moderator' | 'chairperson'

/** Options for the role selector in the speaker form. */
const ROLE_OPTIONS: { label: string; value: SpeakerRole }[] = [
  { label: 'Speaker', value: 'speaker' },
  { label: 'Moderator', value: 'moderator' },
  { label: 'Chairperson', value: 'chairperson' }
]

interface Event {
  id: number
  slug: string
}

interface Speaker {
  id: number
  full_name: string
  email: string | null
  job_title: string | null
  organization: string | null
  phone_number: string | null
  short_bio: string | null
  visible: boolean
  photo_url: string | null
  role: SpeakerRole
}

interface SpeakerDrawerProps {
  event: Event
  speaker: Speaker | null
  /** Default role applied to a newly created speaker (mirrors the tab's active role sub-tab). */
  defaultRole: SpeakerRole
  open: boolean
  onClose: () => void
}

export default function SpeakerDrawer({ event, speaker, defaultRole, open, onClose }: SpeakerDrawerProps) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])

  useEffect(() => {
    if (open) {
      if (speaker) {
        form.setFieldsValue({
          full_name: speaker.full_name,
          email: speaker.email,
          job_title: speaker.job_title,
          organization: speaker.organization,
          phone_number: speaker.phone_number,
          short_bio: speaker.short_bio,
          visible: speaker.visible,
          role: speaker.role
        })
        if (speaker.photo_url) {
          setFileList([{
            uid: '-1',
            name: 'current-photo.jpg',
            status: 'done',
            url: speaker.photo_url
          }])
        } else {
          setFileList([])
        }
      } else {
        form.resetFields()
        form.setFieldsValue({ visible: true, role: defaultRole })
        setFileList([])
      }
    }
  }, [open, speaker, defaultRole, form])

  const handleSubmit = async (values: any) => {
    setLoading(true)

    try {
      const normalize = (value: string | null | undefined) => value ?? ''
      const formData = new FormData()
      formData.append('speaker[full_name]', values.full_name)
      formData.append('speaker[email]', normalize(values.email))
      formData.append('speaker[job_title]', normalize(values.job_title))
      formData.append('speaker[organization]', normalize(values.organization))
      formData.append('speaker[phone_number]', normalize(values.phone_number))
      formData.append('speaker[short_bio]', normalize(values.short_bio))
      formData.append('speaker[visible]', values.visible ? 'true' : 'false')
      formData.append('speaker[role]', values.role)

      // Add photo if there's a new file
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('speaker[photo]', fileList[0].originFileObj)
      }

      let response
      if (speaker) {
        response = await axios.patch(
          `/events/${event.slug}/agenda/speakers/${speaker.id}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        )
      } else {
        response = await axios.post(
          `/events/${event.slug}/agenda/speakers`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        )
      }

      if (response.data.success) {
        message.success(response.data.message)
        router.reload()
        onClose()
      }
    } catch (error: any) {
      message.error(error.response?.data?.errors?.join(', ') || 'Failed to save speaker')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Drawer
      title={speaker ? 'Edit Speaker' : 'Add Speaker'}
      open={open}
      onClose={onClose}
      onSubmit={() => form.submit()}
      submitText={speaker ? 'Update' : 'Add'}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ visible: true }}
        className="compact-form"
      >
        <Form.Item label="Photo" style={{ marginBottom: 8 }}>
          <Upload
            listType="picture-card"
            fileList={fileList}
            onChange={({ fileList }) => setFileList(fileList)}
            beforeUpload={() => false}
            maxCount={1}
            accept="image/*"
          >
            {fileList.length === 0 && (
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Click to Upload</div>
              </div>
            )}
          </Upload>
          <div className="text-xs text-gray-500 mt-1">
            <div>• Maximum upload file size: 2 MB</div>
            <div>• Recommended Dimensions: 320 x 320px</div>
          </div>
        </Form.Item>

        <Form.Item
          label="Full Name"
          name="full_name"
          rules={[{ required: true, message: 'Please enter speaker name' }]}
        >
          <Input placeholder="Enter speaker name" size="small" />
        </Form.Item>

        <Form.Item
          label="Role"
          name="role"
          rules={[{ required: true, message: 'Please select a role' }]}
        >
          <Select placeholder="Select role" size="small" options={ROLE_OPTIONS} />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[{ type: 'email', message: 'Please enter a valid email' }]}
        >
          <Input placeholder="Enter email" size="small" />
        </Form.Item>

        <Form.Item label="Job Title" name="job_title">
          <Input placeholder="Enter job title" size="small" />
        </Form.Item>

        <Form.Item label="Organization" name="organization">
          <Input placeholder="Enter organization" size="small" />
        </Form.Item>

        <Form.Item label="Short Bio" name="short_bio">
          <Input.TextArea
            placeholder="Enter bio"
            rows={4}
            maxLength={400}
            showCount
            size="small"
          />
        </Form.Item>

        <Form.Item label="Phone Number" name="phone_number">
          <Input placeholder="Enter phone number" size="small" />
        </Form.Item>

        <Form.Item
          label="Visibility"
          name="visible"
          valuePropName="checked"
          help="Show/hide profile from public view."
          style={{ marginBottom: 0 }}
        >
          <Switch checkedChildren="Visible" unCheckedChildren="Hidden" />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
